import { useState } from "react";
import ChartRenderer from "./ChartRenderer";
import { placeholderSvg, stoneImage } from "../utils/stoneImage";


// Maths the AI sometimes writes as LaTeX ("\\times", "\\frac{a}{b}", "\\[ … \\]") reads as plain text
function cleanMath(text) {
  return text
    .replace(/\\\(|\\\)|\\\[|\\\]|\$\$/g, "")
    .replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, "($1) / ($2)")
    .replace(/\\text\{([^{}]*)\}/g, "$1")
    .replace(/\\times/g, "×").replace(/\\cdot/g, "·").replace(/\\approx/g, "≈")
    .replace(/\\%/g, "%").replace(/\\,/g, " ");
}

// A stock ID shown in bold ("**5106287**", "**01-06237069**", "**L87-301A**") can be clicked for its details
const STONE_ID_RE = /^(?=(?:\D*\d){4})[A-Z0-9]+(?:[-/][A-Z0-9]+)*$/;

function MarkdownText({ text, onIdClick }) {
  if (!text) return null;

  // Split by newlines first
  const lines = cleanMath(text).split("\n");

  return (
    <div className="text-gray-800 text-sm leading-relaxed space-y-1">
      {lines.map((line, lineIdx) => {
        // Bullet list items
        const bulletMatch = line.match(/^(\s*[-*•])\s+(.*)/);
        if (bulletMatch) {
          return (
            <div key={lineIdx} className="flex gap-2 ml-2">
              <span className="text-gray-400 flex-shrink-0">•</span>
              <span>{renderInline(bulletMatch[2], onIdClick)}</span>
            </div>
          );
        }

        // Numbered list items
        const numMatch = line.match(/^(\s*\d+[.)]\s+)(.*)/);
        if (numMatch) {
          return (
            <div key={lineIdx} className="flex gap-2 ml-2">
              <span className="text-gray-500 flex-shrink-0 font-medium">{numMatch[1].trim()}</span>
              <span>{renderInline(numMatch[2], onIdClick)}</span>
            </div>
          );
        }

        // Heading lines (## or ###)
        const headingMatch = line.match(/^(#{1,3})\s+(.*)/);
        if (headingMatch) {
          const level = headingMatch[1].length;
          const cls = level === 1 ? "font-bold text-base" :
                      level === 2 ? "font-semibold text-sm mt-2" :
                      "font-medium text-sm mt-1";
          return (
            <div key={lineIdx} className={`text-gray-900 ${cls}`}>
              {renderInline(headingMatch[2], onIdClick)}
            </div>
          );
        }

        // "---" is a divider, not text
        if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
          return <hr key={lineIdx} className="border-gray-200 my-1" />;
        }

        // Empty line = spacer
        if (line.trim() === "") {
          return <div key={lineIdx} className="h-1" />;
        }

        // Regular paragraph
        return (
          <div key={lineIdx}>
            {renderInline(line, onIdClick)}
          </div>
        );
      })}
    </div>
  );
}


function renderInline(text, onIdClick) {
  if (!text) return null;

  // Split text into segments using regex for markdown patterns
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    let match = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (match) {
      if (match[1]) parts.push(<span key={key++}>{renderInline(match[1], onIdClick)}</span>);
      const id = match[2].trim();
      parts.push(onIdClick && id.length >= 6 && STONE_ID_RE.test(id) ? (
        <button key={key++} type="button" title={`Show stone ${id}`} onClick={() => onIdClick(id)}
                className="font-semibold text-blue-700 underline decoration-dotted hover:text-blue-900">
          {match[2]}
        </button>
      ) : (
        <strong key={key++} className="font-semibold text-gray-900">{match[2]}</strong>
      ));
      remaining = match[3];
      continue;
    }

    // Italic: *text* — only as a word of its own, so "6.79*6.38*4.28" stays a measurement
    match = remaining.match(/^(.*?)(?<![\w*])\*(?![\s*])([^*]+?)(?<![\s*])\*(?![\w*])(.*)/s);
    if (match) {
      if (match[1]) parts.push(<span key={key++}>{renderInline(match[1], onIdClick)}</span>);
      parts.push(<em key={key++}>{match[2]}</em>);
      remaining = match[3];
      continue;
    }

    // Code: `text`
    match = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (match) {
      if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);
      parts.push(
        <code key={key++} className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">
          {match[2]}
        </code>
      );
      remaining = match[3];
      continue;
    }

    // Markdown link: [text](url)
    match = remaining.match(/^(.*?)\[([^\]]+)\]\(([^)]+)\)(.*)/s);
    if (match) {
      if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);
      parts.push(
        <a key={key++} href={match[3]} target="_blank" rel="noreferrer"
           className="text-blue-600 hover:text-blue-800 underline break-all">
          {match[2]}
        </a>
      );
      remaining = match[4];
      continue;
    }

    // Bare URL: https://... or http://...
    match = remaining.match(/^(.*?)(https?:\/\/[^\s<>)"']+)(.*)/s);
    if (match) {
      if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);
      parts.push(
        <a key={key++} href={match[2]} target="_blank" rel="noreferrer"
           className="text-blue-600 hover:text-blue-800 underline break-all">
          {match[2]}
        </a>
      );
      remaining = match[3];
      continue;
    }

    // No more patterns — push rest as plain text
    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}


// ── Stone Card ─────────────────────────────────────────────────────────────────
// Field names match PostgreSQL repository output (snake_case):
//   stock_id, shape, carat, color, clarity, cut, polish, symmetry,
//   fluorescence, lab, total_price, price_per_carat, measurements,
//   country, location, status, has_image, has_video, has_certificate,
//   certificate_url, is_fancy_color, fancy_color, fancy_intensity
// "$5,386.50": prices always with cents
const money = (v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StoneCard({ stone, onAsk }) {
  const s = stone;

  const specs = [s.cut, s.polish, s.symmetry].filter(Boolean).join(" / ");

  const statusColor =
    s.status === "A" ? "text-emerald-600" :
    s.status === "H" ? "text-amber-500" :
    s.status === "M" ? "text-blue-500" : "text-gray-400";

  const statusLabel =
    s.status === "A" ? "Available" :
    s.status === "H" ? "On Hold" :
    s.status === "M" ? "On Memo" :
    s.status || "—";

  const colorDisplay = s.is_fancy_color
    ? [s.fancy_intensity, s.fancy_color].filter(Boolean).join(" ")
    : s.color;

  // back is negative: -38.5 → "Disc. 38.50%" (below the Rapaport price)
  const discountPct =
    typeof s.back === "number" && s.back < 0
      ? Math.abs(s.back).toFixed(2)
      : null;

  // Real photo only when the link is this stone's own; otherwise a labelled sample
  const image = stoneImage(s);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col">
      {/* Diamond image — CDN URL or local shape/color fallback */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 h-32 flex items-center justify-center relative overflow-hidden">
        <img
            src={image.src}
            alt={s.stock_id}
            className="h-full w-full object-contain"
            onError={(e) => {
              // Photo or sample file failed to load → drawn placeholder
              e.target.onerror = null;
              e.target.src = placeholderSvg(s.is_fancy_color, s.fancy_color);
            }}
        />
        {!image.isReal && (
          <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-white/90 text-gray-500 text-[10px] px-1.5 py-0.5 rounded select-none">
            Sample image
          </span>
        )}
        {s.lab && (
          <span className="absolute top-2 left-2 bg-white border border-gray-200 text-xs font-bold px-1.5 py-0.5 rounded shadow-sm select-none">
            {s.lab}
          </span>
        )}
        {s.is_fancy_color && (
          <span className="absolute top-2 right-2 bg-yellow-100 text-yellow-700 text-xs font-semibold px-1.5 py-0.5 rounded select-none">
            Fancy
          </span>
        )}
      </div>

      {/* Details */}
      <div className="p-3 flex flex-col gap-1 flex-1">
        {/* ID + status */}
        <div className="flex justify-between items-center">
          {s.stock_id && onAsk ? (
            <button type="button" title={`Show stone ${s.stock_id}`} onClick={() => onAsk(s.stock_id)}
                    className="text-xs font-mono text-blue-600 hover:underline">{s.stock_id}</button>
          ) : (
            <span className="text-xs font-mono text-gray-400">{s.stock_id || "—"}</span>
          )}
          <span className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
        </div>

        {/* Main spec line */}
        <div className="text-sm font-semibold text-gray-900 leading-snug">
          {s.shape} {s.carat}ct {colorDisplay} {s.clarity}
        </div>

        {/* Cut / Polish / Symmetry */}
        {specs && (
          <div className="text-xs text-gray-500">{specs}</div>
        )}

        {/* Fluorescence */}
        {s.fluorescence && s.fluorescence !== "NON" && (
          <div className="text-xs text-gray-400">Flu: {s.fluorescence}</div>
        )}

        {/* Measurements */}
        {s.measurements && (
          <div className="text-xs text-gray-400">{s.measurements}</div>
        )}

        {/* Location */}
        {/* {s.location && (
          <div className="text-xs text-gray-400">📍 {s.location}</div>
        )} */}

        {/* Price */}
        <div className="mt-auto pt-2 flex justify-between items-end border-t border-gray-100">
          {s.price_per_carat > 0 && (
            <div className="text-xs text-gray-500">
              ${money(s.price_per_carat)}/ct
            </div>
          )}
          {s.total_price > 0 && (
            <div className="text-base font-bold text-gray-900">
              ${money(s.total_price)}
            </div>
          )}
        </div>

        {/* Discount badge + recommendation badge ("92% match", "Pair 1", "+1 color") */}
        {(discountPct || s.badge) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {discountPct && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                <span className="font-bold">Disc.</span> {discountPct}%
              </span>
            )}
            {s.badge && (
              <span className="inline-flex items-center bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                {s.badge}
              </span>
            )}
          </div>
        )}

        {/* Links */}
        {(s.certificate_url || s.has_certificate || s.video_url || s.has_video) && (
          <div className="flex gap-3 mt-1">
            {s.certificate_url && (
              <a href={s.certificate_url} target="_blank" rel="noreferrer"
                 className="text-xs text-blue-600 hover:underline">
                📄 Cert
              </a>
            )}
            {s.video_url ? (
              <a href={s.video_url} target="_blank" rel="noreferrer"
                 className="text-xs text-blue-600 hover:underline">▶ Video</a>
            ) : s.has_video ? (
              <span className="text-xs text-gray-400">▶ Video</span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// The answer as clean text for the clipboard: the message without markup, one line per stone
function plainText(data) {
  const lines = [];
  if (data.message) {
    lines.push(cleanMath(data.message)
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/^\s*[-*•]\s+/gm, "• ")
      .replace(/^#{1,3}\s+/gm, "")
      .replace(/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/gm, ""));
  }
  for (const s of data.stones || []) {
    const color = s.is_fancy_color ? [s.fancy_intensity, s.fancy_color].filter(Boolean).join(" ") : s.color;
    const spec = [s.shape, s.carat != null ? `${s.carat}ct` : null, color, s.clarity, s.lab].filter(Boolean).join(" ");
    const price = s.total_price > 0
      ? ` — $${money(s.total_price)}${s.price_per_carat > 0 ? ` ($${money(s.price_per_carat)}/ct)` : ""}` : "";
    lines.push(`${s.stock_id || "—"} — ${spec}${price}`);
  }
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // No clipboard API (plain http): copy through a hidden text box
    const box = document.createElement("textarea");
    box.value = text;
    box.style.position = "fixed";
    box.style.opacity = "0";
    document.body.appendChild(box);
    box.select();
    const ok = document.execCommand("copy");
    box.remove();
    return ok;
  }
}

// ── Stone Grid ─────────────────────────────────────────────────────────────────
function StoneGrid({ stones, total, filters, onShowMore, onAsk }) {
  if (!stones?.length) return null;

  return (
    <div className="mt-3">
      <div className="grid grid-cols-3 gap-3">
        {stones.map((s, i) => <StoneCard key={s.stock_id || s.vStnId || i} stone={s} onAsk={onAsk} />)}
      </div>

      {total > stones.length && (
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-400">
            Showing {stones.length} of {total.toLocaleString()} results
          </p>
          <button
            onClick={() => onShowMore?.()}
            className="inline-flex items-center gap-1.5 bg-black text-white text-xs font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            View all {total.toLocaleString()} →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Error Banner ───────────────────────────────────────────────────────────────
function ErrorBanner({ message, detail }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      <span>⚠️</span>
      <div className="min-w-0">
        <p>{message}</p>
        {/* Provider's original error text (e.g. "Rate limit reached ... Please try again in 7m") */}
        {detail && detail !== message && (
          <p className="mt-1.5 text-xs text-red-600/80 break-words">
            <span className="font-medium">Details:</span> {detail}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Chip Suggestions ───────────────────────────────────────────────────────────
function ChipSuggestions({ chips, onSelect }) {
  if (!chips?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {chips.map((chip, i) => (
        <button
          key={i}
          onClick={() => onSelect?.(chip)}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-3 py-1.5 rounded-full transition-colors border border-gray-200"
        >
          {chip}
        </button>
      ))}
    </div>
  );
}

// ── Main BotResponse ───────────────────────────────────────────────────────────
export default function BotResponse({ data, onChipClick, onShowMore, onReply, onClearFilters }) {
  const [copied, setCopied] = useState(false);
  if (!data) return null;

  const isError = data.type === "error";
  const hasChart = !!data.chart;
  const hasStones = data.stones?.length > 0;

  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 max-w-3xl w-full relative group">
      {/* Copy button — the answer as clean text (appears on hover, next to Reply) */}
      {!isError && (data.message || hasStones) && (
        <button
          onClick={async () => {
            if (await copyText(plainText(data))) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
          title={copied ? "Copied" : "Copy this answer"}
          className="absolute -top-2 right-6 opacity-0 group-hover:opacity-100 transition-opacity
                     bg-white border border-gray-200 rounded-full h-7 px-2 flex items-center justify-center
                     shadow-sm hover:bg-gray-50 hover:border-gray-400 text-gray-500 hover:text-gray-800 z-10 text-[11px]"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      )}

      {/* Reply button — appears on hover, top-right corner */}
      {!isError && onReply && (
        <button
          onClick={onReply}
          title="Reply to this message"
          className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity
                     bg-white border border-gray-200 rounded-full w-7 h-7 flex items-center justify-center
                     shadow-sm hover:bg-gray-50 hover:border-gray-400 text-gray-500 hover:text-gray-800 z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
            <path fillRule="evenodd" d="M7.793 2.232a.75.75 0 01-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 010 10.75H10.75a.75.75 0 010-1.5h2.875a3.875 3.875 0 000-7.75H3.622l4.146 3.957a.75.75 0 01-1.036 1.085l-5.5-5.25a.75.75 0 010-1.085l5.5-5.25a.75.75 0 011.061.025z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      {isError ? (
        <ErrorBanner message={data.message} detail={data.error_detail} />
      ) : (
        <>
          {/* Message */}
          {data.message && (
            <div className="mb-2">
              <MarkdownText text={data.message} onIdClick={(id) => onChipClick?.(`Show me the details of stone ${id}`)} />
            </div>
          )}

          {/* Earlier filters were carried into this search — one click to drop them */}
          {data.kept_filters?.length > 0 && onClearFilters && (
            <button
              onClick={onClearFilters}
              className="mb-3 inline-flex items-center gap-1.5 text-xs text-gray-600 border border-gray-300 rounded-full px-3 py-1 hover:bg-gray-50 hover:border-gray-400"
            >
              <span aria-hidden="true">✕</span>
              Clear earlier filters ({data.kept_filters.join(", ")})
            </button>
          )}

          {/* Analytics chart */}
          {hasChart && (
            <>
              {data.chart.title && (
                <h2 className="font-semibold text-gray-900 text-sm mb-2">{data.chart.title}</h2>
              )}
              <ChartRenderer chart={data.chart} />

              {data.narrative && data.narrative !== data.message && (
                <p className="mt-3 text-gray-500 text-xs leading-relaxed">{data.narrative}</p>
              )}

              {data.ai_recommendations?.length > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <h4 className="font-semibold text-amber-900 mb-1.5 text-xs">💡 Recommendations</h4>
                  <ul className="list-disc ml-4 text-xs text-amber-800 space-y-1">
                    {data.ai_recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* More charts in the same answer ("total sales and top-selling shapes") */}
          {data.extra_charts?.map((chart, i) => (
            <div key={i} className="mt-5 pt-4 border-t border-gray-100">
              {chart.title && (
                <h2 className="font-semibold text-gray-900 text-sm mb-2">{chart.title}</h2>
              )}
              <ChartRenderer chart={chart} />
              {data.extra_narratives?.[i] && (
                <p className="mt-3 text-gray-500 text-xs leading-relaxed">{data.extra_narratives[i]}</p>
              )}
            </div>
          ))}

          {/* Stone grid */}
          {hasStones && (
            <StoneGrid
              stones={data.stones}
              total={data.total_count}
              filters={data.filters_applied}
              onShowMore={onShowMore}
              onAsk={(id) => onChipClick?.(`Show me the details of stone ${id}`)}
            />
          )}
        </>
      )}

      {/* Chips */}
      <ChipSuggestions chips={data.clarify_options} onSelect={onChipClick} />
    </div>
  );
}